import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, X } from 'lucide-react';
import { demoStatusLabels, type DemoDomain } from '../../data/demoCenter';
import { easeOutStrong } from '../../utils/motion';
import { DomainIcon } from './DomainIcon';
import { DemoStatusBadge } from './DemoStatusBadge';
type DomainDetailDialogProps = {
  domain: DemoDomain | null;
  onClose: () => void;
};
const focusableSelector = 'a[href], button:not([disabled]), [tabindex]:not([tabindex="-1"])';
export function DomainDetailDialog({
  domain,
  onClose
}: DomainDetailDialogProps) {
  const panelRef = useRef<HTMLDivElement>(null);
  const closeRef = useRef<HTMLButtonElement>(null);
  const reduceMotion = useReducedMotion();
  useEffect(() => {
    if (!domain) return;
    const returnTarget = document.getElementById(`${domain.id}-action`);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    const handleKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault();
        onClose();
        return;
      }
      if (event.key !== 'Tab' || !panelRef.current) return;
      const focusable = Array.from(panelRef.current.querySelectorAll<HTMLElement>(focusableSelector));
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', handleKey);
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = previousOverflow;
      returnTarget?.focus({
        preventScroll: true
      });
    };
  }, [domain, onClose]);
  return <AnimatePresence data-id="element-352" data-mp="1ajs0dizzu419">
      {domain && <motion.div key="overlay" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} transition={{
      duration: 0.2,
      ease: easeOutStrong
    }} className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center bg-navy-deep/40 backdrop-blur-sm p-0 sm:p-6" onMouseDown={event => {
      if (event.target === event.currentTarget) onClose();
    }} data-id="element-353" data-mp="1aiqd8g5pdr36">
          <motion.div ref={panelRef} role="dialog" aria-modal="true" aria-labelledby="domain-dialog-title" aria-describedby="domain-dialog-detail" initial={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 8
      }} animate={{
        opacity: 1,
        scale: 1,
        y: 0
      }} exit={reduceMotion ? {
        opacity: 0
      } : {
        opacity: 0,
        scale: 0.96,
        y: 8
      }} transition={{
        duration: 0.22,
        ease: easeOutStrong
      }} className="relative w-full sm:max-w-2xl max-h-[90vh] overflow-y-auto rounded-t-2xl sm:rounded-2xl border border-white/70 bg-white/95 backdrop-blur-md shadow-premium-hover p-6 sm:p-8" data-id="element-354" data-mp="mvi6ruwjgbzk">
            <div className="flex items-start justify-between gap-4" data-id="element-355" data-mp="25u6fuzon1gnf">
              <div className="flex items-center gap-4" data-id="element-356" data-mp="2ashpb9b0tp54">
                <DomainIcon id={domain.id} size="lg" data-id="element-357" data-mp="2at12ydtn1rdc" />
                <div data-id="element-358" data-mp="2yhkf54ft6oap">
                  <p className="text-sm font-medium text-lavender" data-id="element-359" data-mp="2yj3w6x9ah3dj">{domain.solutionLabel}</p>
                  <h2 id="domain-dialog-title" className="font-heading font-bold text-2xl text-ink-dark leading-snug" data-id="element-360" data-mp="33g4134uek032">
                    {domain.title}
                  </h2>
                </div>
              </div>
              <button ref={closeRef} type="button" onClick={onClose} aria-label="Close" className="shrink-0 rounded-md p-2 text-ink-muted hover:bg-surface-muted hover:text-ink-dark transition-colors duration-150 focus-visible:outline focus-visible:outline-2 focus-visible:outline-teal" data-id="element-361" data-mp="2thocp9mrf4x9">
                <X size={20} aria-hidden="true" data-id="element-362" data-mp="1tj79pb6u08vr" />
              </button>
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-3" data-id="element-363" data-mp="1okdwljvgijw4">
              <DemoStatusBadge status={domain.status} data-id="element-364" data-mp="1jm2n5a92qbef" />
              <span className="text-sm text-ink-muted" data-id="element-365" data-mp="1jnnfct7x1i94">{demoStatusLabels[domain.status].meaning}</span>
            </div>

            <p id="domain-dialog-detail" className="mt-6 text-lg text-ink-dark leading-relaxed" data-id="element-366" data-mp="2h7qgtne5afm9">
              {domain.detail}
            </p>

            <h3 className="mt-8 font-heading font-semibold text-ink-dark mb-3" data-id="element-367" data-mp="27b3v6wdzvkj6">Intended workspaces</h3>
            <ul className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-id="element-368" data-mp="23athsynvhrua">
              {domain.workspaces.map(workspace => <li key={workspace} className="rounded-lg border border-line bg-surface-bg px-4 py-3 font-medium text-ink-dark" data-id="element-369" data-mp="vx9oh612hpg4">
                  {workspace}
                </li>)}
            </ul>

            {domain.workflow.length > 0 && <>
                <h3 className="mt-8 font-heading font-semibold text-ink-dark mb-3" data-id="element-370" data-mp="37hh62kmdxff9">Example workflow</h3>
                <ol className="space-y-2" data-id="element-371" data-mp="3ch3i4qqh8pmh">
                  {domain.workflow.map((step, index) => <li key={step} className="grid grid-cols-[2rem_1fr] items-center gap-3" data-id="element-372" data-mp="3m8q33esma32f" data-mpi={index}>
                      <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-lavender-pale text-sm font-semibold text-lavender" data-id="element-373" data-mp="3mb2flcflvl83">
                        {index + 1}
                      </span>
                      <span className="text-ink-dark" data-id="element-374" data-mp="42uwk2mdgz3t">{step}</span>
                    </li>)}
                </ol>
              </>}

            {domain.note && <p className="mt-8 border-l-4 border-teal bg-teal-pale px-4 py-3 text-ink-dark leading-relaxed" data-id="element-375" data-mp="25u80opgin2yt">
                {domain.note}
              </p>}

            <div className="mt-8 pt-6 border-t border-line flex flex-col sm:flex-row gap-3" data-id="element-376" data-mp="397ht4zk9jb6k">
              <a href="/contact" className="inline-flex items-center justify-center gap-2 rounded-md bg-navy px-5 py-3 font-semibold text-white hover:bg-navy-light transition-colors duration-150 whitespace-nowrap" data-id="element-377" data-mp="348djy8xc9dg8">
                Discuss a pilot
                <ArrowRight size={16} aria-hidden="true" data-id="element-378" data-mp="3s2ezohyot4hm" />
              </a>
              {domain.relatedRoute && <a href={domain.relatedRoute.href} className="inline-flex items-center justify-center gap-2 rounded-md border border-line px-5 py-3 font-semibold text-navy hover:bg-surface-muted transition-colors duration-150 whitespace-nowrap" data-id="element-379" data-mp="3n3c19gd7pwim">
                  {domain.relatedRoute.label}
                </a>}
            </div>
          </motion.div>
        </motion.div>}
    </AnimatePresence>;
}